import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import confetti from "canvas-confetti";

// Milestone configuration (Phase 6 Part 3: 8 tiers up to 10,000)
export const MILESTONES = [
  { threshold: 25,    name: "Rising Track",    emoji: "🔥", color: "from-orange-500 to-red-500",    bgColor: "bg-orange-500/20",  borderColor: "border-orange-500/30" },
  { threshold: 100,   name: "Trending Track",  emoji: "⭐", color: "from-yellow-400 to-amber-500",  bgColor: "bg-yellow-500/20",  borderColor: "border-yellow-500/30" },
  { threshold: 500,   name: "Buzzing Track",   emoji: "📣", color: "from-pink-500 to-rose-500",     bgColor: "bg-pink-500/20",    borderColor: "border-pink-500/30" },
  { threshold: 1000,  name: "RGM Certified",   emoji: "🏆", color: "from-purple-500 to-pink-500",   bgColor: "bg-purple-500/20",  borderColor: "border-purple-500/30" },
  { threshold: 2500,  name: "Breakout Track",  emoji: "💎", color: "from-cyan-400 to-blue-500",     bgColor: "bg-cyan-500/20",    borderColor: "border-cyan-500/30" },
  { threshold: 5000,  name: "Global Track",    emoji: "🌍", color: "from-emerald-400 to-teal-500",  bgColor: "bg-emerald-500/20", borderColor: "border-emerald-500/30" },
  { threshold: 7500,  name: "Viral Track",     emoji: "⚡", color: "from-fuchsia-500 to-violet-500", bgColor: "bg-fuchsia-500/20", borderColor: "border-fuchsia-500/30" },
  { threshold: 10000, name: "Platinum Track",  emoji: "👑", color: "from-amber-300 via-yellow-400 to-amber-500", bgColor: "bg-amber-500/20",   borderColor: "border-amber-400/40" },
];

// Get the highest milestone achieved for a play count
export const getMilestone = (playCount) => {
  let achieved = null;
  for (const milestone of MILESTONES) {
    if (playCount >= milestone.threshold) {
      achieved = milestone;
    }
  }
  return achieved;
};

// Get the next milestone to achieve
export const getNextMilestone = (playCount) => {
  for (const milestone of MILESTONES) {
    if (playCount < milestone.threshold) {
      return milestone;
    }
  }
  return null;
};

// Get progress to next milestone
export const getMilestoneProgress = (playCount) => {
  const nextMilestone = getNextMilestone(playCount);
  if (!nextMilestone) return { percentage: 100, current: playCount, target: playCount };
  
  let prevThreshold = 0;
  for (let i = 0; i < MILESTONES.length; i++) {
    if (MILESTONES[i].threshold === nextMilestone.threshold && i > 0) {
      prevThreshold = MILESTONES[i - 1].threshold;
      break;
    }
  }
  
  const range = nextMilestone.threshold - prevThreshold;
  const progress = playCount - prevThreshold;
  const percentage = Math.min(100, Math.round((progress / range) * 100));
  
  return {
    percentage,
    current: playCount,
    target: nextMilestone.threshold,
    remaining: nextMilestone.threshold - playCount
  };
};

// Small badge for track cards
export const MilestoneBadgeSmall = ({ playCount, className = "" }) => {
  const milestone = getMilestone(playCount);
  if (!milestone) return null;
  
  return (
    <span 
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-bold ${milestone.bgColor} ${milestone.borderColor} border ${className}`}
      title={`${milestone.name} - ${milestone.threshold}+ plays`}
      data-testid="milestone-badge-small"
    >
      <span>{milestone.emoji}</span>
    </span>
  );
};

// Medium badge with name for track pages
export const MilestoneBadgeMedium = ({ playCount, className = "" }) => {
  const milestone = getMilestone(playCount);
  if (!milestone) return null;
  
  return (
    <span 
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-bold bg-gradient-to-r ${milestone.color} text-white shadow-lg ${className}`}
      data-testid="milestone-badge-medium"
    >
      <span>{milestone.emoji}</span>
      <span>{milestone.name}</span>
    </span>
  );
};

// Large badge with full details for track detail pages
export const MilestoneBadgeLarge = ({ playCount, showProgress = true, className = "" }) => {
  const milestone = getMilestone(playCount);
  const nextMilestone = getNextMilestone(playCount);
  const progress = getMilestoneProgress(playCount);
  
  return (
    <div className={`rounded-xl overflow-hidden ${className}`} data-testid="milestone-badge-large">
      {/* Current Milestone */}
      {milestone ? (
        <div className={`p-4 bg-gradient-to-r ${milestone.color} text-white`}>
          <div className="flex items-center gap-3">
            <span className="text-3xl">{milestone.emoji}</span>
            <div>
              <h4 className="font-bold text-lg">{milestone.name}</h4>
              <p className="text-white/80 text-sm">{milestone.threshold.toLocaleString()}+ plays achieved</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="p-4 bg-gray-800 text-white">
          <div className="flex items-center gap-3">
            <span className="text-3xl">🎵</span>
            <div>
              <h4 className="font-bold text-lg">No Milestone Yet</h4>
              <p className="text-gray-400 text-sm">Keep listening to help this track grow!</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Progress to Next Milestone */}
      {showProgress && nextMilestone && (
        <div className="p-4 bg-gray-900 border-t border-gray-800">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-400 text-sm">Next: {nextMilestone.emoji} {nextMilestone.name}</span>
            <span className="text-white text-sm font-semibold">{progress.remaining.toLocaleString()} plays to go</span>
          </div>
          <div className="w-full h-2 bg-gray-800 rounded-full overflow-hidden">
            <div 
              className={`h-full bg-gradient-to-r ${nextMilestone.color} transition-all duration-500`}
              style={{ width: `${progress.percentage}%` }}
            />
          </div>
          <div className="flex justify-between mt-1 text-xs text-gray-500">
            <span>{playCount.toLocaleString()} plays</span>
            <span>{nextMilestone.threshold.toLocaleString()} plays</span>
          </div>
        </div>
      )}
    </div>
  );
};

// Hero tiers get confetti + auto-share prompt
const HERO_TIERS = new Set([1000, 5000, 10000]);

// Animated milestone celebration — Phase 6 Final: confetti + "Share your win?" CTA
export const MilestoneCelebration = ({ milestone, onClose, onShare }) => {
  const timerRef = useRef(null);
  const firedRef = useRef(false);

  useEffect(() => {
    if (!milestone || firedRef.current) return;
    firedRef.current = true;

    // Confetti burst — respect reduced motion
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!prefersReducedMotion) {
      const isHero = HERO_TIERS.has(milestone.threshold);
      const duration = isHero ? 2400 : 1200;
      const end = Date.now() + duration;
      const colors = isHero
        ? ['#fbbf24', '#f59e0b', '#a855f7', '#ec4899', '#14b8a6']
        : ['#a855f7', '#ec4899', '#fbbf24'];

      // Initial celebratory burst
      confetti({
        particleCount: isHero ? 90 : 60,
        spread: 70,
        origin: { y: 0.6 },
        colors,
        scalar: 0.9,
        disableForReducedMotion: true,
      });

      // Sustained side cannons for hero tiers
      if (isHero) {
        const interval = setInterval(() => {
          if (Date.now() > end) {
            clearInterval(interval);
            return;
          }
          confetti({
            particleCount: 22,
            angle: 60,
            spread: 55,
            origin: { x: 0, y: 0.65 },
            colors,
            disableForReducedMotion: true,
          });
          confetti({
            particleCount: 22,
            angle: 120,
            spread: 55,
            origin: { x: 1, y: 0.65 },
            colors,
            disableForReducedMotion: true,
          });
        }, 260);
      }
    }

    // Auto-dismiss after 8s unless user interacts
    timerRef.current = setTimeout(() => {
      onClose && onClose();
    }, 8000);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [milestone, onClose]);

  if (!milestone) return null;
  const isHero = HERO_TIERS.has(milestone.threshold);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm px-4 animate-fadeIn"
      onClick={onClose}
      data-testid="milestone-celebration"
    >
      <div
        className="relative w-full max-w-md rounded-3xl border border-white/15 bg-gradient-to-br from-[#1a1225] via-[#0f0b18] to-[#150a22] p-7 sm:p-8 text-center shadow-2xl animate-bounceIn"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-live="polite"
        data-testid="milestone-celebration-card"
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 h-8 w-8 rounded-full bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-colors"
          data-testid="milestone-celebration-close"
        >
          ×
        </button>

        <div className="text-6xl sm:text-7xl mb-3 animate-bounce" aria-hidden>
          {milestone.emoji}
        </div>
        <p className="text-white/60 text-xs uppercase tracking-[0.25em] font-bold mb-2">
          🎉 Milestone Unlocked
        </p>
        <h2
          className={`text-3xl sm:text-4xl font-black bg-gradient-to-r ${milestone.color} bg-clip-text text-transparent mb-2`}
          data-testid="milestone-celebration-title"
        >
          {milestone.name}!
        </h2>
        <p className="text-white text-lg mb-1">
          You just hit <span className="font-bold">{milestone.threshold.toLocaleString()}</span> plays!
        </p>
        {isHero && (
          <p className="text-white/70 text-sm mb-5">
            This is a big one. Share your win?
          </p>
        )}
        {!isHero && <div className="h-4" />}

        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          {isHero && onShare && (
            <button
              onClick={() => {
                onShare && onShare(milestone.threshold);
                onClose && onClose();
              }}
              className={`flex-1 px-5 py-3 rounded-full bg-gradient-to-r ${milestone.color} text-white font-bold shadow-lg hover:scale-105 active:scale-95 transition-transform inline-flex items-center justify-center gap-2`}
              data-testid="milestone-celebration-share"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
                <polyline points="16 6 12 2 8 6"/>
                <line x1="12" y1="2" x2="12" y2="15"/>
              </svg>
              Share Milestone
            </button>
          )}
          <button
            onClick={onClose}
            className={`${isHero ? 'sm:flex-initial px-5' : 'flex-1 px-5'} py-3 rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold transition-colors`}
            data-testid="milestone-celebration-dismiss"
          >
            {isHero ? 'Later' : 'Awesome! 🎉'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Hook to fetch milestone data from API
export const useMilestoneData = (submissionId) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    if (!submissionId) return;
    
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/track/${submissionId}/milestone`);
        setData(response.data);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [submissionId]);
  
  return { data, loading, error };
};

export default {
  MILESTONES,
  getMilestone,
  getNextMilestone,
  getMilestoneProgress,
  MilestoneBadgeSmall,
  MilestoneBadgeMedium,
  MilestoneBadgeLarge,
  MilestoneCelebration,
  useMilestoneData
};
